import { createSlice } from '@reduxjs/toolkit'
import {
  fetchAllReviews,
  fetchReviewById,
  fetchReviewAnalytics,
  markReviewAsOpened,
  fetchReviewOpenedStatus,
  createReview,
  updateReview,
  deleteReview,
  flagReview,
  voteOnReview,
  fetchPropertyReviews
} from '../actions/reviewActions.js'

// Helper function to apply filters
const applyFilters = (reviews, filters) => {
  // Ensure reviews is an array before filtering
  if (!reviews || !Array.isArray(reviews)) {
    return []
  }

  return reviews.filter(review => {
    // Ensure review object exists
    if (!review) return false

    // Filter by review type
    if (filters.reviewType && review.reviewType !== filters.reviewType) {
      return false
    }

    // Filter by rating
    if (filters.rating && review.rating !== parseInt(filters.rating)) {
      return false
    }

    // Filter by date range
    if (filters.dateRange && filters.dateRange.startDate && filters.dateRange.endDate) {
      const reviewDate = new Date(review.createdAt || review.date || review.updatedAt)
      const startDate = new Date(filters.dateRange.startDate)
      const endDate = new Date(filters.dateRange.endDate)

      // Set time boundaries
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(23, 59, 59, 999)

      if (reviewDate < startDate || reviewDate > endDate) {
        return false
      }
    }

    // Filter by search term (search in title and comment)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      const titleMatch = review.title?.toLowerCase().includes(searchLower)
      const commentMatch = review.comment?.toLowerCase().includes(searchLower)
      if (!titleMatch && !commentMatch) {
        return false
      }
    }

    return true
  })
}

const initialState = {
  allReviews: [],
  selectedReview: null,
  reviewAnalytics: null,
  propertyReviews: {},
  openedStatusMap: {},
  filters: {
    reviewType: '',
    rating: '',
    dateRange: null,
    searchTerm: ''
  },
  filteredReviews: [],
  loading: false,
  error: null,
}

const reviewSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    clearReviewError: (state) => {
      state.error = null
    },
    clearReviewData: (state) => {
      state.allReviews = []
      state.selectedReview = null
      state.reviewAnalytics = null
      state.propertyReviews = {}
      state.openedStatusMap = {}
      state.filteredReviews = []
      state.error = null
    },
    setSelectedReview: (state, action) => {
      state.selectedReview = action.payload
    },
    setReviewFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
      // Apply filters immediately
      state.filteredReviews = applyFilters(state.allReviews, state.filters)
    },
    clearReviewFilters: (state) => {
      state.filters = {
        reviewType: '',
        rating: '',
        dateRange: null,
        searchTerm: ''
      }
      state.filteredReviews = state.allReviews
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all reviews
      .addCase(fetchAllReviews.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllReviews.fulfilled, (state, action) => {
        state.loading = false
        const reviewsData = action.payload?.data?.reviews || action.payload?.data || action.payload
        state.allReviews = Array.isArray(reviewsData) ? reviewsData : []
        state.filteredReviews = applyFilters(state.allReviews, state.filters)
      })
      .addCase(fetchAllReviews.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch single review by ID
      .addCase(fetchReviewById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReviewById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedReview = action.payload.data || action.payload
      })
      .addCase(fetchReviewById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch review analytics
      .addCase(fetchReviewAnalytics.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReviewAnalytics.fulfilled, (state, action) => {
        state.loading = false
        state.reviewAnalytics = action.payload.data || action.payload
      })
      .addCase(fetchReviewAnalytics.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Mark review as opened
      .addCase(markReviewAsOpened.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(markReviewAsOpened.fulfilled, (state, action) => {
        state.loading = false
        const { reviewId } = action.payload

        // Update opened status map
        state.openedStatusMap[reviewId] = true

        // Update the review in allReviews if it exists and is an array
        if (Array.isArray(state.allReviews)) {
          const reviewIndex = state.allReviews.findIndex(review => review._id === reviewId)
          if (reviewIndex !== -1) {
            state.allReviews[reviewIndex] = {
              ...state.allReviews[reviewIndex],
              isOpened: true
            }
          }
        }

        // Update selected review if it matches
        if (state.selectedReview && state.selectedReview._id === reviewId) {
          state.selectedReview = {
            ...state.selectedReview,
            isOpened: true
          }
        }
      })
      .addCase(markReviewAsOpened.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch review opened status
      .addCase(fetchReviewOpenedStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReviewOpenedStatus.fulfilled, (state, action) => {
        state.loading = false
        const { reviewId, isOpened } = action.payload
        state.openedStatusMap[reviewId] = isOpened
      })
      .addCase(fetchReviewOpenedStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Create review
      .addCase(createReview.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.loading = false
        const newReview = action.payload.data || action.payload
        if (newReview && Array.isArray(state.allReviews)) {
          state.allReviews.unshift(newReview)
        }
        state.filteredReviews = applyFilters(state.allReviews, state.filters)
      })
      .addCase(createReview.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update review
      .addCase(updateReview.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateReview.fulfilled, (state, action) => {
        state.loading = false
        const { reviewId } = action.payload
        const updatedReview = action.payload.data || action.payload

        // Update in allReviews if it's an array
        if (Array.isArray(state.allReviews)) {
          const reviewIndex = state.allReviews.findIndex(review => review._id === reviewId)
          if (reviewIndex !== -1) {
            state.allReviews[reviewIndex] = { ...state.allReviews[reviewIndex], ...updatedReview }
          }
        }

        // Update selected review if it matches
        if (state.selectedReview && state.selectedReview._id === reviewId) {
          state.selectedReview = { ...state.selectedReview, ...updatedReview }
        }

        // Update filtered reviews
        state.filteredReviews = applyFilters(state.allReviews, state.filters)
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Delete review
      .addCase(deleteReview.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.loading = false
        const { reviewId } = action.payload

        // Remove from allReviews if it's an array
        if (Array.isArray(state.allReviews)) {
          state.allReviews = state.allReviews.filter(review => review._id !== reviewId)
        }

        // Clear selected review if it matches
        if (state.selectedReview && state.selectedReview._id === reviewId) {
          state.selectedReview = null
        }

        // Update filtered reviews
        state.filteredReviews = applyFilters(state.allReviews, state.filters)

        // Remove from opened status map
        delete state.openedStatusMap[reviewId]
      })
      .addCase(deleteReview.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Flag review
      .addCase(flagReview.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(flagReview.fulfilled, (state, action) => {
        state.loading = false
        const { reviewId } = action.payload

        // Update review flag status in allReviews if it's an array
        if (Array.isArray(state.allReviews)) {
          const reviewIndex = state.allReviews.findIndex(review => review._id === reviewId)
          if (reviewIndex !== -1) {
            state.allReviews[reviewIndex] = {
              ...state.allReviews[reviewIndex],
              isFlagged: true,
              flaggedAt: new Date().toISOString()
            }
          }
        }

        // Update filtered reviews
        state.filteredReviews = applyFilters(state.allReviews, state.filters)
      })
      .addCase(flagReview.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Vote on review
      .addCase(voteOnReview.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(voteOnReview.fulfilled, (state, action) => {
        state.loading = false
        const { reviewId } = action.payload
        const voteData = action.payload.data || action.payload

        // Update review vote counts in allReviews if it's an array
        if (Array.isArray(state.allReviews)) {
          const reviewIndex = state.allReviews.findIndex(review => review._id === reviewId)
          if (reviewIndex !== -1) {
            state.allReviews[reviewIndex] = {
              ...state.allReviews[reviewIndex],
              ...voteData
            }
          }
        }

        // Update selected review if it matches
        if (state.selectedReview && state.selectedReview._id === reviewId) {
          state.selectedReview = { ...state.selectedReview, ...voteData }
        }

        // Update filtered reviews
        state.filteredReviews = applyFilters(state.allReviews, state.filters)
      })
      .addCase(voteOnReview.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch property reviews
      .addCase(fetchPropertyReviews.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPropertyReviews.fulfilled, (state, action) => {
        state.loading = false
        const { propertyId, reviews } = action.payload
        state.propertyReviews[propertyId] = reviews.data || reviews
      })
      .addCase(fetchPropertyReviews.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  clearReviewError,
  clearReviewData,
  setSelectedReview,
  setReviewFilters,
  clearReviewFilters
} = reviewSlice.actions

export default reviewSlice.reducer